import { ProcedimentoSelectModel } from 'components/form/field/select/ProcedimentoSelectField'
import {
  isProcedimentoSisab,
  shouldUseProcedimentoReferencia,
} from 'components/form/field/select/ProcedimentoSelectField/ProcedimentoSelectFieldItem'
import {
  ExameEspecificoEnum,
  FichaComplementarZikaMicrocefaliaResultadoExameEnum,
  OpcoesResultadoExameQualitativoEnum,
  ResultadoExamePreNatalInput,
} from 'graphql/types.generated'
import { isUndefinedOrNull } from 'util/checks'
import { formatNumber } from 'util/number'
import { range, required, validate } from 'util/validation'

import { convertResultadoExameToResultadoExamePreNatalInput } from '../../aside/pre-natal/converter-acompanhamentoPreNatal'
import { ConverterResultadoExameType } from '../converter-objetivo'
import { ResultadoExameMarcadoresBioquimicosDecimalField } from './adicionar/exames-especificos/ResultadoExameMarcadoresBioquimicosDecimalField'
import { ResultadoExameMarcadoresBioquimicosNumberField } from './adicionar/exames-especificos/ResultadoExameMarcadoresBioquimicosNumberField'
import { ResultadoExamePrenatalField } from './adicionar/exames-especificos/ResultadoExamePrenatalField'
import { ResultadoExamePuericulturaField } from './adicionar/exames-especificos/ResultadoExamePuericulturaField'
import { ResultadoExameQualitativoField } from './adicionar/exames-especificos/ResultadoExameQualitativoField'
import {
  validateResultadoExamePrenatal,
  validateResultadoExamePuericultura,
  validateResultadoExameQualitativo,
} from './adicionar/validator-ResultadosExamesObjetivo'
import {
  AllResultadoEspecificoValueType,
  CodigosExamesEspecificos,
  ExameQueryModel,
  FactoryResultadoExameEspecificoFieldProps,
  FactoryResultadoExameEspecificoModel,
  FactoryResultadoExameEspecificoProperties,
  FactoryResultadoExameEspecificoViewProps,
  ResultadoEspecificoPrenatal,
  ResultadoExameEspecificoModel,
  ResultadosExamesModel,
} from './model-resultadosExames'
import {
  convertResultadoExameMarcadoresBioquimicosToInput,
  convertResultadoExameMarcadoresBioquimicosValueToModel,
  convertResultadoExamePrenatalToInput,
  convertResultadoExamePrenatalValueToModel,
  convertResultadoExamePuericulturaToInput,
  convertResultadoExamePuericulturaValueToModel,
  convertResultadoExameQualitativoToInput,
  convertResultadoExameQualitativoValueToModel,
} from './visualizar/exames-especificos/converter-ExamesEspecificos'
import {
  extractResultadoExameMarcadoresBioquimicosValueComFlag,
  extractResultadoExameMarcadoresBioquimicosValueSemFlag,
  extractResultadoExamePrenatalValue,
  extractResultadoExamePuericulturaValue,
  extractResultadoExameQualitativoValue,
} from './visualizar/exames-especificos/extract'
import { ResultadoExameMarcadoresBioquimicosView } from './visualizar/exames-especificos/ResultadoExameMarcadoresBioquimicosView'
import { ResultadoExamePrenatalView } from './visualizar/exames-especificos/ResultadoExamePrenatalView'
import { ResultadoExamePuericulturaView } from './visualizar/exames-especificos/ResultadoExamePuericulturaView'
import { ResultadoExameQualitativoView } from './visualizar/exames-especificos/ResultadoExameQualitativoView'

const RESULTADOS_EXAMES_PREFIX = 'id'

const formatResultadoExameEspecificoDecimal = (
  value: ResultadoExameEspecificoModel,
  exame: ExameQueryModel,
  flagPilotoCasaDecimal: boolean
): ResultadoExameEspecificoModel => {
  const { resultado, ...prevValue } = value
  const { precision, maxLength } = getResultadoExameEspecificoProperties(exame, flagPilotoCasaDecimal)

  const tmpValue = formatNumber(resultado, precision).slice(0, maxLength)
  const lastChar = tmpValue.slice(-1)
  const newValue = lastChar === ',' ? tmpValue.slice(0, -1) : tmpValue

  return {
    ...prevValue,
    resultado: newValue as AllResultadoEspecificoValueType,
  }
}

export const resultadosExamesEspecificosFactory = (
  flagPilotoCasaDecimal: boolean
): Record<CodigosExamesEspecificos, FactoryResultadoExameEspecificoModel> => ({
  '0202010503': {
    validator: ({ value }) => validate(value, [required, range(0, 100)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: (input, exame) =>
      convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame, flagPilotoCasaDecimal),
    extractValueFromQuery: flagPilotoCasaDecimal
      ? extractResultadoExameMarcadoresBioquimicosValueComFlag
      : extractResultadoExameMarcadoresBioquimicosValueSemFlag,
    formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame, flagPilotoCasaDecimal),
    components: {
      ResultadoField: ResultadoExameMarcadoresBioquimicosDecimalField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: 6,
      precision: 2,
      unit: '%',
      relatedDbEnum: ExameEspecificoEnum.HEMOGLOBINA_GLICADA,
    },
  },
  '0202010295': {
    validator: ({ value }) => validate(value, [required, range(1, 10000)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: (input, exame) =>
      convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame, flagPilotoCasaDecimal),
    extractValueFromQuery: flagPilotoCasaDecimal
      ? extractResultadoExameMarcadoresBioquimicosValueComFlag
      : extractResultadoExameMarcadoresBioquimicosValueSemFlag,
    ...(flagPilotoCasaDecimal && {
      formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame, flagPilotoCasaDecimal),
    }),
    components: {
      ResultadoField: flagPilotoCasaDecimal
        ? ResultadoExameMarcadoresBioquimicosDecimalField
        : ResultadoExameMarcadoresBioquimicosNumberField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: flagPilotoCasaDecimal ? 8 : 5,
      ...(flagPilotoCasaDecimal && { precision: 2 }),
      unit: 'mg/dL',
      relatedDbEnum: ExameEspecificoEnum.COLESTEROL_TOTAL,
    },
  },
  '0202010279': {
    validator: ({ value }) => validate(value, [required, range(1, 10000)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: (input, exame) =>
      convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame, flagPilotoCasaDecimal),
    extractValueFromQuery: flagPilotoCasaDecimal
      ? extractResultadoExameMarcadoresBioquimicosValueComFlag
      : extractResultadoExameMarcadoresBioquimicosValueSemFlag,
    ...(flagPilotoCasaDecimal && {
      formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame, flagPilotoCasaDecimal),
    }),
    components: {
      ResultadoField: flagPilotoCasaDecimal
        ? ResultadoExameMarcadoresBioquimicosDecimalField
        : ResultadoExameMarcadoresBioquimicosNumberField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: flagPilotoCasaDecimal ? 8 : 5,
      ...(flagPilotoCasaDecimal && { precision: 2 }),
      unit: 'mg/dL',
      relatedDbEnum: ExameEspecificoEnum.COLESTEROL_HDL,
    },
  },
  '0202010287': {
    validator: ({ value }) => validate(value, [required, range(1, 10000)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: (input, exame) =>
      convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame, flagPilotoCasaDecimal),
    extractValueFromQuery: flagPilotoCasaDecimal
      ? extractResultadoExameMarcadoresBioquimicosValueComFlag
      : extractResultadoExameMarcadoresBioquimicosValueSemFlag,
    ...(flagPilotoCasaDecimal && {
      formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame, flagPilotoCasaDecimal),
    }),
    components: {
      ResultadoField: flagPilotoCasaDecimal
        ? ResultadoExameMarcadoresBioquimicosDecimalField
        : ResultadoExameMarcadoresBioquimicosNumberField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: flagPilotoCasaDecimal ? 8 : 5,
      ...(flagPilotoCasaDecimal && { precision: 2 }),
      unit: 'mg/dL',
      relatedDbEnum: ExameEspecificoEnum.COLESTEROL_LDL,
    },
  },
  '0202010678': {
    validator: ({ value }) => validate(value, [required, range(1, 10000)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: (input, exame) =>
      convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame, flagPilotoCasaDecimal),
    extractValueFromQuery: flagPilotoCasaDecimal
      ? extractResultadoExameMarcadoresBioquimicosValueComFlag
      : extractResultadoExameMarcadoresBioquimicosValueSemFlag,
    ...(flagPilotoCasaDecimal && {
      formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame, flagPilotoCasaDecimal),
    }),
    components: {
      ResultadoField: flagPilotoCasaDecimal
        ? ResultadoExameMarcadoresBioquimicosDecimalField
        : ResultadoExameMarcadoresBioquimicosNumberField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: flagPilotoCasaDecimal ? 8 : 5,
      ...(flagPilotoCasaDecimal && { precision: 2 }),
      unit: 'mg/dL',
      relatedDbEnum: ExameEspecificoEnum.TRIGLICERIDIOS,
    },
  },
  '0202010317': {
    validator: ({ value }) => validate(value, [required, range(0.1, 500)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: (input, exame) =>
      convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame, flagPilotoCasaDecimal),
    extractValueFromQuery: flagPilotoCasaDecimal
      ? extractResultadoExameMarcadoresBioquimicosValueComFlag
      : extractResultadoExameMarcadoresBioquimicosValueSemFlag,
    formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame, flagPilotoCasaDecimal),
    components: {
      ResultadoField: ResultadoExameMarcadoresBioquimicosDecimalField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: flagPilotoCasaDecimal ? 6 : 5,
      precision: flagPilotoCasaDecimal ? 2 : 1,
      unit: 'mg/dL',
      relatedDbEnum: ExameEspecificoEnum.DOSAGEM_DE_CREATINA,
    },
  },
  '0202050025': {
    validator: ({ value }) => validate(value, [required, range(0.001, 1000)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: (input, exame) =>
      convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame, flagPilotoCasaDecimal),
    extractValueFromQuery: flagPilotoCasaDecimal
      ? extractResultadoExameMarcadoresBioquimicosValueComFlag
      : extractResultadoExameMarcadoresBioquimicosValueSemFlag,
    formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame, flagPilotoCasaDecimal),
    components: {
      ResultadoField: ResultadoExameMarcadoresBioquimicosDecimalField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: 8,
      precision: 3,
      unit: 'mL/min/1,73 m²',
      relatedDbEnum: ExameEspecificoEnum.CLEARANCE_DE_CREATINA,
    },
  },
  '0205020143': {
    validator: validateResultadoExamePrenatal,
    converterToInput: (value) => convertResultadoExamePrenatalToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: convertResultadoExamePrenatalValueToModel,
    extractValueFromQuery: extractResultadoExamePrenatalValue,
    components: {
      ResultadoField: ResultadoExamePrenatalField,
      ResultadoView: ResultadoExamePrenatalView,
    },
    properties: {
      relatedDbEnum: ExameEspecificoEnum.PRENATAL,
    },
  },
  '0205020151': {
    validator: validateResultadoExamePrenatal,
    converterToInput: (value) => convertResultadoExamePrenatalToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: convertResultadoExamePrenatalValueToModel,
    extractValueFromQuery: extractResultadoExamePrenatalValue,
    components: {
      ResultadoField: ResultadoExamePrenatalField,
      ResultadoView: ResultadoExamePrenatalView,
    },
    properties: {
      relatedDbEnum: ExameEspecificoEnum.PRENATAL,
    },
  },
  '0205010059': {
    validator: validateResultadoExamePrenatal,
    converterToInput: (value) => convertResultadoExamePrenatalToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: convertResultadoExamePrenatalValueToModel,
    extractValueFromQuery: extractResultadoExamePrenatalValue,
    components: {
      ResultadoField: ResultadoExamePrenatalField,
      ResultadoView: ResultadoExamePrenatalView,
    },
    properties: {
      relatedDbEnum: ExameEspecificoEnum.PRENATAL,
    },
  },
  '0211070149': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.ORELHINHA_FALHOU,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.ORELHINHA_PASSOU,
      ],
      relatedDbEnum: ExameEspecificoEnum.PEATEAB,
    },
  },
  '0211070270': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.ORELHINHA_FALHOU,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.ORELHINHA_PASSOU,
      ],
      relatedDbEnum: ExameEspecificoEnum.PEATEST,
    },
  },
  '0205020178': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TRANSFONTANELA_NORMAL,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TRANSFONTANELA_SUGESTIVO_INFECCAO_CONGENITA,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TRANSFONTANELA_OUTRAS_ALTERACOES,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TRANSFONTANELA_INDETERMINADO,
      ],
      relatedDbEnum: ExameEspecificoEnum.EXAMES_IMAGEM_US,
    },
  },
  '0206010079': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TOMOGRAFIA_NORMAL,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TOMOGRAFIA_SUGESTIVO_INFECCAO_CONGENITA,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TOMOGRAFIA_OUTRAS_ALTERACOES,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TOMOGRAFIA_INDETERMINADO,
      ],
      relatedDbEnum: ExameEspecificoEnum.EXAMES_IMAGEM_TC,
    },
  },
  '0207010064': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.RESSONANCIA_NORMAL,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.RESSONANCIA_SUGESTIVO_INFECCAO_CONGENITA,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.RESSONANCIA_OUTRAS_ALTERACOES,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.RESSONANCIA_INDETERMINADO,
      ],
      relatedDbEnum: ExameEspecificoEnum.EXAMES_IMAGEM_RMN,
    },
  },
  '0211060100': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.FUNDO_OLHO_NORMAL,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.FUNDO_OLHO_ALTERADO,
      ],
      relatedDbEnum: ExameEspecificoEnum.FUNDOSCOPIA,
    },
  },
  ABEX022: {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.PRESENTE_BILATERAL,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.DUVIDOSO_OU_AUSENTE,
      ],
      relatedDbEnum: ExameEspecificoEnum.TESTE_OLHINHO,
    },
  },
  '0202020509': {
    validator: validateResultadoExameQualitativo,
    converterToInput: (value) => convertResultadoExameQualitativoToInput(value, flagPilotoCasaDecimal),
    converterValueToModel: convertResultadoExameQualitativoValueToModel,
    extractValueFromQuery: extractResultadoExameQualitativoValue,
    components: {
      ResultadoField: ResultadoExameQualitativoField,
      ResultadoView: ResultadoExameQualitativoView,
    },
    properties: {
      options: [
        OpcoesResultadoExameQualitativoEnum.PROVA_LACO_POSITIVO,
        OpcoesResultadoExameQualitativoEnum.PROVA_LACO_NEGATIVO,
        OpcoesResultadoExameQualitativoEnum.PROVA_LACO_INCONCLUSIVO,
      ],
      relatedDbEnum: ExameEspecificoEnum.PROVA_DO_LACO,
    },
  },
})

const examesEspecificosKeys = Object.keys(resultadosExamesEspecificosFactory(null))

export const mapResultadosPuericulturaEnumLabel: Record<FichaComplementarZikaMicrocefaliaResultadoExameEnum, string> = {
  ORELHINHA_FALHOU: 'Falhou',
  ORELHINHA_PASSOU: 'Passou',
  TRANSFONTANELA_NORMAL: 'Normal',
  TRANSFONTANELA_SUGESTIVO_INFECCAO_CONGENITA: 'Sugestivo de infecção congênita',
  TRANSFONTANELA_OUTRAS_ALTERACOES: 'Outras alterações',
  TRANSFONTANELA_INDETERMINADO: 'Indeterminado',
  TOMOGRAFIA_NORMAL: 'Normal',
  TOMOGRAFIA_SUGESTIVO_INFECCAO_CONGENITA: 'Sugestivo de infecção congênita',
  TOMOGRAFIA_OUTRAS_ALTERACOES: 'Outras alterações',
  TOMOGRAFIA_INDETERMINADO: 'Indeterminado',
  RESSONANCIA_NORMAL: 'Normal',
  RESSONANCIA_SUGESTIVO_INFECCAO_CONGENITA: 'Sugestivo de infecção congênita',
  RESSONANCIA_OUTRAS_ALTERACOES: 'Outras alterações',
  RESSONANCIA_INDETERMINADO: 'Indeterminado',
  FUNDO_OLHO_NORMAL: 'Normal',
  FUNDO_OLHO_ALTERADO: 'Alterado',
  DUVIDOSO_OU_AUSENTE: 'Duvidoso ou ausente',
  PRESENTE_BILATERAL: 'Presente bilateral',
}

export const mapResultadosQualitativosEnumLabel: Record<OpcoesResultadoExameQualitativoEnum, string> = {
  PROVA_LACO_POSITIVO: 'Positivo',
  PROVA_LACO_NEGATIVO: 'Negativo',
  PROVA_LACO_INCONCLUSIVO: 'Inconclusivo',
}

export const isExameEspecifico = (exame: ExameQueryModel, flagDengue: boolean): boolean => {
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  const isProvaLaco = codigo === '0202020509'
  if (!flagDengue && isProvaLaco) return false
  return examesEspecificosKeys.includes(codigo)
}

export const showCodigoProcedimento = (codigo: string): string => {
  return isProcedimentoSisab(codigo) ? 'CDS' : codigo
}

export const getResultadoExameEspecificoProperties = (
  exame: ExameQueryModel,
  flagPilotoCasaDecimal: boolean
): FactoryResultadoExameEspecificoProperties => {
  const factory = resultadosExamesEspecificosFactory(flagPilotoCasaDecimal)
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.properties || {}
}

export const getResultadoExameEspecificoField = (
  exame: ExameQueryModel,
  flagPilotoCasaDecimal: boolean
): React.ComponentType<FactoryResultadoExameEspecificoFieldProps> => {
  const factory = resultadosExamesEspecificosFactory(flagPilotoCasaDecimal)
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.components?.ResultadoField
}

export const getResultadoExameEspecificoView = (
  exame: ExameQueryModel,
  flagPilotoCasaDecimal: boolean
): React.ComponentType<FactoryResultadoExameEspecificoViewProps> => {
  const factory = resultadosExamesEspecificosFactory(flagPilotoCasaDecimal)
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.components?.ResultadoView
}

export const getResultadoExameEspecificoValidator = (
  exame: ExameQueryModel,
  flagPilotoCasaDecimal: boolean
): FactoryResultadoExameEspecificoModel['validator'] => {
  const factory = resultadosExamesEspecificosFactory(flagPilotoCasaDecimal)
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.validator
}

export const getResultadoExameEspecificoConverterToInput = (
  exame: ExameQueryModel,
  flagPilotoCasaDecimal: boolean
): FactoryResultadoExameEspecificoModel['converterToInput'] => {
  const factory = resultadosExamesEspecificosFactory(flagPilotoCasaDecimal)
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.converterToInput
}

export const getResultadoExameEspecificoConverterToModel = (
  exame: ExameQueryModel,
  flagPilotoCasaDecimal: boolean
): FactoryResultadoExameEspecificoModel['converterValueToModel'] => {
  const factory = resultadosExamesEspecificosFactory(flagPilotoCasaDecimal)
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.converterValueToModel
}

export const getResultadoExameEspecificoFormatOnSave = (
  exame: ExameQueryModel,
  flagPilotoCasaDecimal: boolean
): FactoryResultadoExameEspecificoModel['formatOnSave'] => {
  const factory = resultadosExamesEspecificosFactory(flagPilotoCasaDecimal)
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.formatOnSave
}

export const getResultadoExameEspecificoExtractValueFromQuery = (
  exame: ExameQueryModel,
  flagPilotoCasaDecimal: boolean
): FactoryResultadoExameEspecificoModel['extractValueFromQuery'] => {
  const factory = resultadosExamesEspecificosFactory(flagPilotoCasaDecimal)
  const codigo = shouldUseProcedimentoReferencia(exame?.codigo) ? exame?.procedimentoReferencia?.codigo : exame?.codigo
  return factory?.[codigo]?.extractValueFromQuery
}

export const getResultadoExameEspecificoPuericulturaLabel = (
  value: FichaComplementarZikaMicrocefaliaResultadoExameEnum
): string => {
  return mapResultadosPuericulturaEnumLabel[value]
}

export const getResultadoExameQualitativoLabel = (value: OpcoesResultadoExameQualitativoEnum): string => {
  return mapResultadosQualitativosEnumLabel[value]
}

export const addPrefix = (value: ID): string => RESULTADOS_EXAMES_PREFIX + value
export const removePrefix = (value: string): ID => value.replace(RESULTADOS_EXAMES_PREFIX, '')

export const isResultadoExamePreNatal = (resultadoExame: ResultadoExameEspecificoModel): boolean => {
  const resultadoPreNatal = resultadoExame?.resultado as ResultadoEspecificoPrenatal
  return !isUndefinedOrNull(resultadoPreNatal?.dpp) || !isUndefinedOrNull(resultadoPreNatal?.idadeGestacional)
}

export const filterResultadosExamesAndConvertToExamePreNatalInput = (
  resultadosExames: ResultadosExamesModel,
  filter: (resultado: ResultadoExameEspecificoModel) => boolean
): ResultadoExamePreNatalInput[] => {
  const { resultadosComSolicitacao, resultadosSemSolicitacao } = resultadosExames

  const resultadosComSolicitacaoFiltradosInput = resultadosComSolicitacao
    ? Object.entries(resultadosComSolicitacao)
        .filter(([_, value]) => filter(value))
        .map(([id, value]) => convertResultadoExameToResultadoExamePreNatalInput(value as ConverterResultadoExameType))
    : []

  const resultadosSemSolicitacaoFiltradosInput = resultadosSemSolicitacao
    ? Object.values(resultadosSemSolicitacao)
        .filter(filter)
        .map((value) => convertResultadoExameToResultadoExamePreNatalInput(value))
    : []

  return [...resultadosComSolicitacaoFiltradosInput, ...resultadosSemSolicitacaoFiltradosInput]
}

export const PROCEDIMENTOS_ABEX_EXCEPTION_CODES: Set<string> = new Set(['ABEX012', 'ABEX013', 'ABEX022'])

export const nomeCodigoProcedimentoRef = (exame: ProcedimentoSelectModel): [string, string] => {
  if (PROCEDIMENTOS_ABEX_EXCEPTION_CODES.has(exame.codigo)) {
    return [exame.descricao, 'CDS']
  }

  const shouldUseProcedimentoRef = shouldUseProcedimentoReferencia(exame.codigo)
  const codigo = shouldUseProcedimentoRef ? exame.procedimentoReferencia?.codigo : exame.codigo
  const nome = shouldUseProcedimentoRef ? exame.procedimentoReferencia?.descricao : exame.descricao

  return [nome, codigo]
}

export const showCodigoProcedimentoRef = (exame: ProcedimentoSelectModel, ocultaCodigo: boolean = false): string => {
  const [nome, codigo] = nomeCodigoProcedimentoRef(exame)

  return `${nome}${ocultaCodigo ? '' : ` - ${codigo}`}`
}
